import React, { useState } from "react";
import type { ProductoGrupo, ProductoSubgrupo } from "@/lib/api";
import { RubrosListado } from "./RubrosListado";
import { RubrosABM } from "./RubrosABM";
import { SubrubrosListado } from "./SubrubrosListado";
import { SubrubrosABM } from "./SubrubrosABM";
import { useRegisterOpcionesAbmNav } from "../opcionesAbmBreadcrumb";

type RubrosView = "Listado" | "ABM";
type SubrubrosView = "Listado" | "ABM";

export function RubrosSubrubrosSection() {
  const [includeInactive, setIncludeInactive] = useState(false);

  const [rubrosView, setRubrosView] = useState<RubrosView>("Listado");
  const [rubroSel, setRubroSel] = useState<ProductoGrupo | null>(null);
  const [rubrosRefresh, setRubrosRefresh] = useState(0);

  const [subView, setSubView] = useState<SubrubrosView>("Listado");
  const [subSel, setSubSel] = useState<ProductoSubgrupo | null>(null);
  const [subRefresh, setSubRefresh] = useState(0);

  const bumpRubros = () => setRubrosRefresh((n) => n + 1);
  const bumpSub = () => setSubRefresh((n) => n + 1);

  const goToLists = () => {
    setRubroSel(null);
    setRubrosView("Listado");
    setSubSel(null);
    setSubView("Listado");
  };

  const rubroAbm = rubrosView === "ABM";
  const subAbm = subView === "ABM";
  const isAbm = rubroAbm || subAbm;
  const isEdit = rubroAbm && !subAbm
    ? rubroSel != null
    : subAbm && !rubroAbm
      ? subSel != null
      : Boolean(rubroSel || subSel);

  useRegisterOpcionesAbmNav(isAbm, isEdit, goToLists);

  return (
    <div className="space-y-12">
      <label className="flex items-center gap-2 text-sm cursor-pointer select-none w-fit mb-2">
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
          className="w-4 h-4 rounded border-input"
        />
        <span>Incluir inactivos</span>
      </label>

      <section className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">Rubros</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Grupos de producto usados en stock y fichas de artículos.
          </p>
        </div>

        {rubrosView === "ABM" ? (
          <RubrosABM
            item={rubroSel}
            onBack={() => {
              setRubroSel(null);
              setRubrosView("Listado");
            }}
            onSaved={bumpRubros}
          />
        ) : (
          <RubrosListado
            refreshTrigger={rubrosRefresh}
            includeInactive={includeInactive}
            onListChanged={() => {
              bumpRubros();
              bumpSub();
            }}
            onEdit={(row) => {
              setRubroSel(row);
              setRubrosView("ABM");
            }}
            onNew={() => {
              setRubroSel(null);
              setRubrosView("ABM");
            }}
          />
        )}
      </section>

      <section className="space-y-3 border-t border-border pt-10">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">Subrubros</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Subgrupos asociados a un rubro. Eliminar un rubro también quita sus subrubros en base de
            datos.
          </p>
        </div>

        {subView === "ABM" ? (
          <SubrubrosABM
            item={subSel}
            onBack={() => {
              setSubSel(null);
              setSubView("Listado");
            }}
            onSaved={bumpSub}
            includeInactiveGrupos={includeInactive}
          />
        ) : (
          <SubrubrosListado
            refreshTrigger={subRefresh}
            includeInactive={includeInactive}
            onListChanged={bumpSub}
            onEdit={(row) => {
              setSubSel(row);
              setSubView("ABM");
            }}
            onNew={() => {
              setSubSel(null);
              setSubView("ABM");
            }}
          />
        )}
      </section>
    </div>
  );
}
